import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Modal,
  Tab,
  Grid,
  Checkbox,
  Input,
  Icon,
  Button,
} from 'semantic-ui-react';
import _ from 'lodash';
import ReactTable from 'react-table';

import {
  ReimbursableMealFieldEnum as ReimbMealFields,
  MenuRecipeFieldEnum as MenuRecipeFields,
} from 'Common/FieldEnum';
import {
  changeReimbMealContribution,
  removeRecipeFromReimbMeal,
  addRecipeToReimbMeal,
  resetReimbMealContributions,
} from 'Pages/managemenu/state/editmenu/actions';
import { getRecipeMapOnDay } from 'Pages/managemenu/state/editmenu/selectors';
import { RecipeCategoryEnum } from 'Common/Constants/Enumeration/RecipeCategoryEnum';
import { PopupTextCell } from 'Common/GridHelper';
import { getValueFromInputMode } from 'Common/GeneralHelper';
import { InputMode } from 'Common/FormEnum';

import {
  computeMealContributionValues,
  getMealContributionMessage,
  getMealContributionValues,
  formatDayText,
} from '../utilities';
import { REIMBURSABLE_MEAL_TABLE_ROWS_MAP } from '../constants';

class ReimbursableMealModal extends Component {
  static propTypes = {
    modalProps: PropTypes.object,
    recipesById: PropTypes.object,
    dailyViewDay: PropTypes.number,
    menuTitle: PropTypes.string,
    showDaysOfTheWeek: PropTypes.bool,
    analysisDays: PropTypes.number,
  };

  state = {
    tabIndex: 0,
    vegTotalsState: [],
  };

  componentDidUpdate(prevProps) {
    const {
      meals,
      modalProps: { open },
    } = this.props;
    const startingVegTotalState = [];

    if (!prevProps.modalProps.open && open) {
      meals.forEach((meal) => {
        const darkGreenVeg =
          parseFloat(meal[ReimbMealFields.DarkGreenVeg]) || 0;
        const redOrangeVeg =
          parseFloat(meal[ReimbMealFields.RedOrangeVeg]) || 0;
        const legumesVeg = parseFloat(meal[ReimbMealFields.LegumesVeg]) || 0;
        const starchyVeg = parseFloat(meal[ReimbMealFields.StarchyVeg]) || 0;
        const otherVeg = parseFloat(meal[ReimbMealFields.OtherVeg]) || 0;
        const additionalVeg =
          parseFloat(meal[ReimbMealFields.AdditionalVeg]) || 0;

        const computedVegTotal =
          darkGreenVeg +
          redOrangeVeg +
          legumesVeg +
          starchyVeg +
          otherVeg +
          additionalVeg;

        startingVegTotalState.push({
          RecipeId: meal.RecipeId,
          ManuallyEdited: computedVegTotal !== meal.TotalVeg,
        });
      });
      this.setState({ vegTotalsState: startingVegTotalState });
    }
  }

  onCheckboxToggle = (mealRecipeId, recipe) => (e, data) => {
    const { addRecipeToReimbMeal, removeRecipeFromReimbMeal, dailyViewDay } =
      this.props;
    const { checked } = data;

    if (checked) {
      addRecipeToReimbMeal({ mealRecipeId, dayNumber: dailyViewDay, recipe });
    } else {
      removeRecipeFromReimbMeal({
        mealRecipeId,
        dayNumber: dailyViewDay,
        recipe,
      });
    }
  };

  getMealContributionTableRows(
    currentContributionValues,
    computedContributionValues
  ) {
    const rowsMap = { ...REIMBURSABLE_MEAL_TABLE_ROWS_MAP };
    const { TotalGrain, ...editableFields } = ReimbMealFields;

    Object.keys(editableFields).forEach((field) => {
      if (
        currentContributionValues[field] !== computedContributionValues[field]
      ) {
        rowsMap[field] = { ...rowsMap[field] };

        const label = rowsMap[field].label;
        rowsMap[field].label = `*${label}`;
      }
    });

    return Object.values(rowsMap);
  }

  getMealRecipeTableColumns(meal) {
    const { recipesOnDay, isReadOnly } = this.props;
    const { RecipeId } = meal;
    const entree = recipesOnDay[RecipeId];

    return [
      {
        resizable: false,
        id: 'ToggleMealRecipe',
        accessor: (row) => row,
        Cell: (row) => {
          const recipe = row.value;

          return (
            <Checkbox
              disabled={Boolean(
                recipe.RecipeId === RecipeId ||
                  entree.Children[recipe.RecipeId] ||
                  isReadOnly
              )}
              checked={Boolean(
                recipe.RecipeId === RecipeId ||
                  entree.Children[recipe.RecipeId] ||
                  meal.Children[recipe.RecipeId]
              )}
              onChange={this.onCheckboxToggle(RecipeId, recipe)}
            />
          );
        },
        width: 35,
      },
      {
        accessor: MenuRecipeFields.Name,
        resizable: false,
        Cell: (row) => <PopupTextCell text={row.value} />,
      },
      {
        id: 'ServingSize',
        Header: 'Serving Size',
        resizable: false,
        accessor: (row) =>
          `${row[MenuRecipeFields.QtyOfPortion]} ${
            row[MenuRecipeFields.RecipeMeasure]
          }`,
        Cell: (row) => {
          return <PopupTextCell text={`${row.value}`} />;
        },
      },
      {
        id: 'Contributions',
        Header: 'Contributions',
        resizable: false,
        accessor: (row) => row,
        Cell: (row) => {
          return <PopupTextCell text={getMealContributionMessage(row.value)} />;
        },
      },
    ];
  }

  getMealContributionsTableColumns(meal) {
    const { RecipeId } = meal;

    return [
      {
        resizable: false,
        accessor: 'label',
        Cell: (row) => row.value,
      },
      {
        resizable: false,
        id: 'ContributionInput',
        accessor: (row) => row,
        Cell: (row) => {
          const { field, disabled, unit } = row.value;

          return (
            <Input
              className="contribution-input"
              fluid
              value={meal[field]}
              onChange={this.onContributionChange(field, RecipeId)}
              onBlur={this.onContributionBlur(field, RecipeId)}
              label={unit}
              labelPosition="right"
              disabled={disabled || this.props.isReadOnly}
            />
          );
        },
      },
    ];
  }

  onContributionChange = (field, recipeId) => (e) => {
    const { changeReimbMealContribution, dailyViewDay } = this.props;
    const { vegTotalsState } = this.state;
    const {
      target: { value },
    } = e;

    const parsedValue = getValueFromInputMode(InputMode.Decimal, [6, 3], value);
    const mealTotalVegState = vegTotalsState.find(
      (x) => x.RecipeId === recipeId
    );

    changeReimbMealContribution({
      field,
      recipeId,
      value: parsedValue,
      dayNumber: dailyViewDay,
      hasVegTotalBeenEdited: mealTotalVegState.ManuallyEdited,
    });

    if (field === ReimbMealFields.TotalVeg) {
      this.updateTotalVegState(true, recipeId);
    }
  };

  onContributionBlur = (field, recipeId) => (e) => {
    const { changeReimbMealContribution, dailyViewDay } = this.props;
    const { vegTotalsState } = this.state;
    const {
      target: { value },
    } = e;

    const mealTotalVegState = vegTotalsState.find(
      (x) => x.RecipeId === recipeId
    );

    if (_.isEmpty(value)) {
      changeReimbMealContribution({
        field,
        recipeId,
        value: 0,
        dayNumber: dailyViewDay,
        hasVegTotalBeenEdited: mealTotalVegState.ManuallyEdited,
      });
    }
  };

  onTabChange = (e, data) => {
    const { activeIndex } = data;

    this.setState({ tabIndex: activeIndex });
  };

  get tabPanes() {
    const { meals, recipesOnDay } = this.props;

    const tabs = meals.map((meal) => {
      return {
        menuItem: meal.Description,
        render: () => {
          const { RecipeId } = meal;

          const entree = recipesOnDay[RecipeId];
          const entreeRecipes = Object.values(entree.Children);
          const remainingRecipes = _.pickBy(recipesOnDay, (recipe) => {
            if (
              recipe.RecipeId === RecipeId ||
              recipe.RecipeCategoryId === RecipeCategoryEnum.Entree
            ) {
              return false;
            }
            return true;
          });
          const recipes = [
            entree,
            ...entreeRecipes,
            ...Object.values(remainingRecipes),
          ];

          return (
            <Tab.Pane>
              <ReactTable
                columns={this.getMealRecipeTableColumns(meal)}
                data={recipes}
                showPageSizeOptions={false}
              />
            </Tab.Pane>
          );
        },
      };
    });

    return tabs;
  }

  renderModalContent() {
    const {
      meals,
      recipesOnDay,
      modalProps: { open },
    } = this.props;
    const { tabIndex } = this.state;

    if (!open) {
      return null;
    }

    if (meals.length > 0) {
      const meal = meals[tabIndex];
      const currentContributionValues = getMealContributionValues(meal);
      const computedContributionValues = computeMealContributionValues(
        meal,
        recipesOnDay
      );
      const contributionTableColumns = this.getMealContributionsTableColumns(
        meals[tabIndex]
      );

      return (
        <Grid>
          <Grid.Column width={10}>
            <Tab
              className="meal-tabs"
              tabIndex={tabIndex}
              panes={this.tabPanes}
              onTabChange={this.onTabChange}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <Grid>
              <Grid.Row className="contributions-header">
                <Grid.Column>Contributions</Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  * Contributions that have been manually modified will need to
                  be adjusted manually.
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <ReactTable
                  columns={contributionTableColumns}
                  data={this.getMealContributionTableRows(
                    currentContributionValues,
                    computedContributionValues
                  )}
                  showPageSizeOptions={false}
                  minRows={contributionTableColumns.length}
                  showPagination={false}
                />
              </Grid.Row>
            </Grid>
          </Grid.Column>
        </Grid>
      );
    }

    return (
      <Grid>
        <Grid.Column width={16}>
          <span>There are no meals on the current day.</span>
        </Grid.Column>
      </Grid>
    );
  }

  resetContributions = () => {
    const { meals, resetReimbMealContributions } = this.props;
    const { tabIndex } = this.state;

    const currentMeal = meals[tabIndex];
    const { RecipeId, DayNumber } = currentMeal;

    this.updateTotalVegState(false, RecipeId);

    resetReimbMealContributions({ recipeId: RecipeId, dayNumber: DayNumber });
  };

  onClose = () => {
    const {
      modalProps: { onClose },
    } = this.props;

    if (onClose) {
      this.setState({ tabIndex: 0 });
      this.setState({ vegTotalsState: [] });
      onClose();
    }
  };

  updateTotalVegState = (wasEdited, recipeId) => {
    const { vegTotalsState } = this.state;
    const newVegTotalsState = vegTotalsState.map((vegState) => {
      if (vegState.RecipeId === recipeId) {
        vegState.ManuallyEdited = wasEdited;
      }
      return vegState;
    });

    this.setState({ vegTotalsState: newVegTotalsState });
  };

  render() {
    const {
      modalProps,
      menuTitle,
      dailyViewDay,
      showDaysOfTheWeek,
      analysisDays,
      meals,
      isReadOnly,
    } = this.props;

    return (
      <Modal {...modalProps} size="fullscreen">
        <Modal.Header className="manage-menu-modal-header">
          {`${menuTitle} Meals - ${formatDayText({
            dayNumber: dailyViewDay,
            showDaysOfTheWeek,
            analysisDays,
          })}`}
          <Icon
            name="close"
            onClick={this.onClose}
            className="manage-menu-close"
          />
        </Modal.Header>
        <Modal.Content className="manage-menu-modal-content">
          {this.renderModalContent()}
        </Modal.Content>
        <Modal.Actions>
          {Array.isArray(meals) && meals.length > 0 && (
            <Button
              className="mnm-button-blue"
              onClick={this.resetContributions}
              disabled={isReadOnly}
            >
              Reset Contributions
            </Button>
          )}
        </Modal.Actions>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { dailyViewDay } = ownProps;

  return {
    recipesOnDay: getRecipeMapOnDay(state)(dailyViewDay),
  };
};

const mapDispatchToProps = {
  changeReimbMealContribution,
  addRecipeToReimbMeal,
  removeRecipeFromReimbMeal,
  resetReimbMealContributions,
};

ReimbursableMealModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(ReimbursableMealModal);

export { ReimbursableMealModal };



// WEBPACK FOOTER //
// ./src/pages/managemenu/subpages/editmenu/components/ReimbursableMealModal.js